import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle2, 
  LayoutDashboard, 
  Bell,
  Activity,
  ShieldCheck,
  User,
  Search,
  Clock,
  Filter,
  MoreHorizontal,
  RefreshCw,
  MessageSquare,
  ArrowRight
} from 'lucide-react';

const PracticeManagerSlide = () => {
  const [tasks, setTasks] = useState([
    { 
        id: 1, 
        patient: "Sarah Smith", 
        type: "Verification", 
        title: "Coverage Inactive",
        message: "Hey Sarah, could you check if this patient shared new coverage info? I ran a check and it seems like their coverage is inactive.",
        status: "unassigned", 
        time: "Just now",
        priority: "high"
    },
    { 
        id: 2, 
        patient: "John Doe", 
        type: "Recall", 
        title: "6mo Hygiene Due", 
        message: "Patient is due for hygiene. Please call to schedule.",
        status: "unassigned", 
        time: "2 min ago",
        priority: "medium"
    },
    { 
        id: 3, 
        patient: "Mike Jones", 
        type: "Billing", 
        title: "Balance Overdue", 
        message: "Patient has an outstanding balance of $150. Send payment reminder.",
        status: "unassigned", 
        time: "5 min ago",
        priority: "medium"
    },
  ]);

  const [completedCount, setCompletedCount] = useState(12);

  // Simulation Loop
  useEffect(() => {
    let step = 0;
    const interval = setInterval(() => {
      setTasks(prev => {
        const newTasks = [...prev];
        
        // Cycle through assigning tasks
        if (step === 0) {
           // Reset for loop
           return [
            { 
                id: 1, 
                patient: "Sarah Smith", 
                type: "Verification", 
                title: "Coverage Inactive",
                message: "Hey Sarah, could you check if this patient shared new coverage info? I ran a check and it seems like their coverage is inactive.",
                status: "unassigned", 
                time: "Just now",
                priority: "high"
            },
            { 
                id: 2, 
                patient: "John Doe", 
                type: "Recall", 
                title: "6mo Hygiene Due", 
                message: "Patient is due for hygiene. Please call to schedule.",
                status: "unassigned", 
                time: "2 min ago",
                priority: "medium"
            },
            { 
                id: 3, 
                patient: "Mike Jones", 
                type: "Billing", 
                title: "Balance Overdue", 
                message: "Patient has an outstanding balance of $150. Send payment reminder.",
                status: "unassigned", 
                time: "5 min ago",
                priority: "medium"
            },
           ];
        } else if (step === 1) {
            newTasks[0] = { ...newTasks[0], status: "analyzing" };
        } else if (step === 2) {
            newTasks[0] = { ...newTasks[0], status: "assigned", assignee: "Sarah", avatar: "sarah" };
        } else if (step === 3) {
            newTasks[1] = { ...newTasks[1], status: "analyzing" };
        } else if (step === 4) {
            newTasks[1] = { ...newTasks[1], status: "assigned", assignee: "Stephanie", avatar: "stephanie" };
        } else if (step === 5) {
            newTasks[2] = { ...newTasks[2], status: "analyzing" };
        } else if (step === 6) {
            newTasks[2] = { ...newTasks[2], status: "assigned", assignee: "Kianna", avatar: "kianna" };
        }
        
        return newTasks;
      });

      step = (step + 1) % 8; // 8 steps total loop
    }, 1500); // Slower loop to read text

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-8 xl:p-12 relative overflow-hidden font-sans">
        {/* Background Gradients */}
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-100/50 rounded-full blur-[100px] -translate-y-1/3 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-indigo-100/50 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/3" />

        {/* Floating Header */}
        <div className="absolute top-8 left-8 xl:left-12 z-50 flex items-center gap-4 bg-white/60 backdrop-blur-md p-2 pr-6 rounded-full border border-white/40 shadow-sm">
            <div className="w-12 h-12 rounded-full bg-white overflow-hidden shadow-md border border-slate-100">
                 <img src="/assets/trisha.png" alt="Trisha" className="w-full h-full object-cover" />
            </div>
            <div>
                <h3 className="text-base font-bold text-slate-900 leading-none">Trisha</h3>
                <p className="text-[10px] text-blue-600 font-bold uppercase tracking-wide mt-1">Practice Manager</p>
            </div>
        </div>

        <div className="flex flex-col xl:flex-row h-full w-full max-w-[1600px] gap-12 relative z-10 pt-24 xl:pt-0 items-center">
            
            {/* LEFT SIDE: Content */}
            <div className="w-full xl:w-[35%] flex flex-col space-y-8 z-20">
                <div className="space-y-6">
                    <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                        AI that intelligently <span className="text-blue-600">delegates tasks</span> to your staff.
                    </h2>
                    <p className="text-xl text-slate-600 leading-relaxed font-light">
                        She doesn't just do tasks—she manages them.
                        <br/><br/>
                        <span className="font-medium text-slate-900">Trisha</span> works from your data in Asha PMS, identifies revenue opportunities, and assigns specific tasks to your human team to ensure <strong className="text-blue-600">Inbox Zero</strong> every day.
                    </p>
                </div>
            </div>

            {/* RIGHT SIDE: Visual Dashboard */}
            <div className="w-full xl:w-[65%] h-full flex items-center justify-center relative">
                
                {/* Dashboard Frame */}
                <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-3xl overflow-hidden flex flex-col h-[600px] relative">
                    
                    {/* Header / Toolbar */}
                    <div className="h-20 border-b border-slate-100 flex items-center justify-between px-8 bg-slate-50/50">
                        <div className="flex items-center gap-4">
                            <h3 className="font-bold text-slate-800 text-xl">Task Board</h3>
                            
                            {/* Feature 1: PMS Monitoring Indicator */}
                            <div className="flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-full border border-green-100 text-xs font-bold animate-pulse">
                                <RefreshCw className="w-3 h-3" />
                                <span>PMS Sync Active</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            {/* Feature 3: Daily Accountability Counter */}
                            <div className="flex items-center gap-3">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">Daily Goal</span>
                                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border border-slate-200 shadow-sm">
                                    <CheckCircle2 className="w-5 h-5 text-blue-600" />
                                    <span className="text-base font-bold text-slate-700">{completedCount} / 15 Done</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Filters Bar */}
                    <div className="h-14 border-b border-slate-100 flex items-center px-8 gap-4 text-sm text-slate-500 bg-white">
                        <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg text-slate-900 font-bold cursor-pointer transition-colors hover:bg-slate-200">
                            <LayoutDashboard className="w-4 h-4" />
                            All Tasks
                        </div>
                        <div className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors">
                            <Filter className="w-4 h-4" />
                            Filter
                        </div>
                        <div className="flex-1" />
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input type="text" placeholder="Search tasks..." className="pl-9 pr-4 py-2 rounded-lg bg-slate-50 border border-transparent focus:bg-white focus:border-blue-200 text-sm w-56 focus:outline-none transition-all" />
                        </div>
                    </div>

                    {/* Task Feed */}
                    <div className="flex-1 bg-slate-50/50 p-8 space-y-4 overflow-hidden relative">
                        <AnimatePresence mode="popLayout">
                            {tasks.map((task, index) => (
                                <TaskCard key={task.id} task={task} isFeatured={index === 0} />
                            ))}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Floating Labels */}
                <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    className="absolute top-8 left-[5%] bg-white px-4 py-2 rounded-xl shadow-xl border border-green-100 text-sm font-bold text-green-700 flex items-center gap-2 pointer-events-none z-20"
                >
                    <Activity className="w-4 h-4" />
                    PMS Monitoring
                    <div className="absolute top-1/2 -right-1.5 w-3 h-3 bg-white transform rotate-45 border-t border-r border-green-100" />
                </motion.div>

                <motion.div 
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.8 }}
                    className="absolute top-24 right-[8%] bg-white px-4 py-2 rounded-xl shadow-xl border border-blue-100 text-sm font-bold text-blue-700 flex items-center gap-2 pointer-events-none z-20"
                >
                    Daily Accountability
                    <div className="absolute top-1/2 -left-1.5 w-3 h-3 bg-white transform -rotate-45 border-t border-l border-blue-100" />
                    <CheckCircle2 className="w-4 h-4" />
                </motion.div>

            </div>
        </div>
    </div>
  );
};

const TaskCard = ({ task, isFeatured }) => {
    return (
        <motion.div 
            layout
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`
                relative bg-white rounded-2xl border shadow-sm transition-all duration-500 overflow-hidden
                ${isFeatured 
                    ? 'border-blue-200 shadow-xl shadow-blue-500/10 scale-100 z-10' 
                    : 'border-slate-200 scale-[0.98] opacity-60 grayscale-[0.5]'
                }
            `}
        >
            {/* Status Bar */}
            <div className={`h-1 w-full ${isFeatured ? 'bg-blue-500' : 'bg-slate-200'}`} />

            <div className="p-5">
                <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 text-sm font-bold">
                            {task.patient.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                            <h4 className="font-bold text-slate-900 text-base">{task.title}</h4>
                            <p className="text-xs text-slate-500 font-medium flex items-center gap-1">
                                <User className="w-3 h-3" /> {task.patient}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-400">{task.time}</span>
                        {task.priority === 'high' && (
                            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" title="High Priority" />
                        )}
                    </div>
                </div>

                {/* Message Bubble Area */}
                {isFeatured && (
                    <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="bg-slate-50 rounded-xl p-4 mb-4 relative"
                    >
                        <div className="absolute top-0 left-6 w-3 h-3 bg-slate-50 transform -translate-y-1/2 rotate-45" />
                        <div className="flex gap-3">
                            <div className="w-8 h-8 rounded-full bg-white border border-slate-200 overflow-hidden flex-shrink-0">
                                <img src="/assets/trisha.png" alt="Trisha" className="w-full h-full object-cover" />
                            </div>
                            <div>
                                <p className="text-xs font-bold text-slate-900 mb-1">Trisha <span className="text-slate-400 font-normal ml-1">AI Practice Manager</span></p>
                                <p className="text-sm text-slate-700 leading-relaxed">
                                    {task.message}
                                </p>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Footer / Assignment Action */}
                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">Status:</span>
                        
                        {task.status === "unassigned" && (
                            <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-md">Pending Review</span>
                        )}
                        
                        {task.status === "analyzing" && (
                            <motion.div 
                                initial={{ opacity: 0 }} 
                                animate={{ opacity: 1 }}
                                className="flex items-center gap-2 text-blue-600 bg-blue-50 px-2 py-1 rounded-md"
                            >
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                <span className="text-xs font-bold">Matching Staff...</span>
                            </motion.div>
                        )}

                        {task.status === "assigned" && (
                            <motion.div 
                                initial={{ opacity: 0, scale: 0.9 }} 
                                animate={{ opacity: 1, scale: 1 }}
                                className="flex items-center gap-2 text-green-700 bg-green-50 px-2 py-1 rounded-md border border-green-100"
                            >
                                <CheckCircle2 className="w-3 h-3" />
                                <span className="text-xs font-bold">Assigned</span>
                            </motion.div>
                        )}
                    </div>

                    <div className="flex items-center">
                        {task.status === "assigned" ? (
                            <motion.div 
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="flex items-center gap-2"
                            >
                                <span className="text-xs text-slate-500 mr-1">to</span>
                                <div className="flex items-center gap-2 pl-1 pr-3 py-1 bg-white border border-slate-200 rounded-full shadow-sm">
                                    <div className="w-6 h-6 rounded-full bg-slate-100 overflow-hidden">
                                        <img src={`/assets/${task.avatar}.png`} alt={task.assignee} className="w-full h-full object-cover" />
                                    </div>
                                    <span className="text-xs font-bold text-slate-800">{task.assignee}</span>
                                </div>
                            </motion.div>
                        ) : (
                            <div className="w-8 h-8 rounded-full bg-slate-50 border border-slate-100 border-dashed flex items-center justify-center">
                                <User className="w-4 h-4 text-slate-300" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default PracticeManagerSlide;
